import { z } from 'zod';
import { createDateTimeSchema, dateTimeUtcStringSchema, getDefaults, lt, Validation } from '@mews/framework';
import { ExportFormat, ExportScheduleFrequency, FixedInterval } from '@mews-commander/data-access';
import { SchemaFormDefinition } from '@mews-commander/form';
import { Option } from '@mews-commander/types';

export const exportConfigSchema = z.object({
    name: Validation.String.Required.default(''),
    reportInterval: z.nativeEnum(FixedInterval),
    format: z.nativeEnum(ExportFormat).default(ExportFormat.Excel),
    includePayoutDetails: z.boolean().default(false),
});

export const exportConfigFormDefinition: SchemaFormDefinition<typeof exportConfigSchema> = {
    fields: {
        name: { label: lt('ExportName') },
        reportInterval: {
            label: lt('Interval'),
            field: {
                type: 'enum',
                enumPrefix: 'FixedInterval',
            },
        },
        format: {
            label: lt('Format'),
            field: {
                type: 'enum',
                enumPrefix: 'ExportFormat',
                omitHint: true,
            },
        },
        includePayoutDetails: {
            label: lt('PayoutExportIncludePayoutDetailsLabel'),
            field: {
                type: 'checkbox',
            },
        },
    },
};

export const scheduleConfigSchema = z
    .object({
        frequency: z.nativeEnum(ExportScheduleFrequency).default(ExportScheduleFrequency.Weekly),
        startUtc: dateTimeUtcStringSchema.default(''),
        exportTargetId: z.string().uuid().nullable().default(null),
        isEnabled: z.boolean().default(true),
        notifyCreatorAboutExport: z.boolean().default(true),
    })
    .extend<{}>(createDateTimeSchema({ fieldName: 'startUtc', required: true }));

export const getScheduleConfigFormDefinition: (
    exportTargets: Option[],
    isEditing?: boolean
) => SchemaFormDefinition<typeof scheduleConfigSchema> = (exportTargets: Option[], isEditing = false) => ({
    fields: {
        frequency: {
            label: lt('Frequency'),
            field: {
                type: 'enum',
                enumPrefix: 'ExportScheduleFrequency',
                omitHint: true,
            },
        },
        startUtc: {
            label: lt('StartDateAndTime'),
            field: {
                type: 'dateTime',
            },
            required: true,
        },
        exportTargetId: {
            label: lt('TargetOptional'),
            field: {
                type: 'select',
                options: exportTargets,
                emptyValueAsNull: true,
            },
        },
        isEnabled: {
            label: isEditing ? lt('PayoutExportEditEnabledLabel') : lt('PayoutExportEnabledLabel'),
            field: {
                type: 'checkbox',
            },
        },
        notifyCreatorAboutExport: {
            label: lt('PayoutExportNotifyLabel'),
            field: {
                type: 'checkbox',
            },
        },
    },
});

export const combinedSchema = exportConfigSchema.merge(scheduleConfigSchema);

export type CombinedSchemaType = z.infer<typeof combinedSchema>;

export const defaultValues = { ...getDefaults(exportConfigSchema), ...getDefaults(scheduleConfigSchema) };
